import { inject } from "@angular/core";
import {
  CanActivateFn,
  Router,
  UrlTree,
} from "@angular/router";

import { sessionStore } from "@auvious/utils";
import { InteractionService } from "../../services/interaction.service";
import { KEY_COBROWSE_INTERACTION } from "../../app.enums";
import { Interaction, InteractionFactory } from "../../models/Interaction";
import { NotificationService } from "../../../core-ui/services/notification.service";
import { CobrowseService } from "../../../core-ui/services/cobrowse.service";
import { ActivityIndicatorService } from "../../../core-ui/services/activity-indicator.service";
import {
  EndpointTypeEnum,
  KEY_COBROWSE_CONTEXT,
  KEY_COBROWSE_CURRENT_SENDER_ENDPOINT,
  KEY_COBROWSE_OLD_SENDER_ENDPOINTS,
  KEY_COBROWSE_REQUEST_TYPE,
  KEY_COBROWSE_SESSION_ID,
} from "../../../core-ui";

export const cobrowseGuard: CanActivateFn = async (
  route,
  state,
): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const interactionService = inject(InteractionService);
  const notification = inject(NotificationService);
  const cobrowse = inject(CobrowseService);
  const activity = inject(ActivityIndicatorService);

  const leave = (): UrlTree => {
    sessionStore.removeItem(KEY_COBROWSE_INTERACTION);
    sessionStore.removeItem(KEY_COBROWSE_SESSION_ID);
    sessionStore.removeItem(KEY_COBROWSE_REQUEST_TYPE);
    sessionStore.removeItem(KEY_COBROWSE_CONTEXT);
    sessionStore.removeItem(KEY_COBROWSE_CURRENT_SENDER_ENDPOINT);
    sessionStore.removeItem(KEY_COBROWSE_OLD_SENDER_ENDPOINTS);
    // leave, no interaction
    notification.error("Co-browse failed", {
      body: "Could not find and restore a previous session",
    });
    sessionStore.removeItem(KEY_COBROWSE_INTERACTION);
    // go back to welcome
    return router.createUrlTree(["/", "welcome"], {
      queryParamsHandling: "preserve",
    });
  };

  return new Promise(async (resolve) => {
    // try restore a previous interaction
    activity.loading(true);
    let interaction = interactionService.getActiveInteraction();
    if (!interaction) {
      try {
        const interactionJSON = sessionStore.getItem(
          KEY_COBROWSE_INTERACTION
        );
        interaction = InteractionFactory.fromJSON(interactionJSON);
        interactionService.setActiveInteraction(interaction);

        const requestType =
          (sessionStore.getItem(
            KEY_COBROWSE_REQUEST_TYPE
          ) as EndpointTypeEnum) || undefined;

        // wait for the participant to join to leave
        cobrowse.participantJoined$.subscribe((p) => {
          activity.loading(false);
          !p ? leave() : resolve(true);
        });

        try {
          // we have a previous interaction, try join the session
          await cobrowse.join(interaction.getRoom(), requestType);
          return resolve(true);
        } catch (e) {
          activity.loading(false);
          resolve(leave());
        }
      } catch (ex) {
        activity.loading(false);
        return resolve(leave());
      }
    } else {
      activity.loading(false);
      // store interaction
      const str = interaction.toString();
      sessionStore.setItem(KEY_COBROWSE_INTERACTION, str);

      const requestType = cobrowse.getRemoteEndpointRequestType();
      sessionStore.setItem(KEY_COBROWSE_REQUEST_TYPE, requestType);
      resolve(true);
    }
  });
};
